/**
 * A small hand-rolled TypeScript tokenizer for the docs code samples.
 *
 * Ported from the legacy `docs/automation/components/CodeBlock.tsx`. v5's
 * CodeBlock originally shipped without highlighting ("intentionally omitted"),
 * which was a silent regression on every guide and reference page — the samples
 * are the densest thing on those pages and read as a wall of grey without it.
 *
 * Promoted here from the docs template bake-off (`features/docs/preview/`,
 * deleted) when variant C was locked in. The palette is no longer a parameter:
 * there is one docs code theme now, declared below.
 */

export type TokenType =
  | "keyword"
  | "string"
  | "number"
  | "comment"
  | "function"
  | "operator"
  | "punctuation"
  | "property"
  | "type"
  | "plain";

export interface Token {
  type: TokenType;
  content: string;
}

export type TokenPalette = Record<TokenType, string>;

/**
 * The docs code theme, built from app tokens rather than raw Tailwind hues so
 * the blocks stay on-palette with the rest of the app.
 *
 * NOTE on purple: `--chart-4` was pulled out of all app *chrome* in the
 * 2026-07-31 brand sweep. A purple survives here for the `keyword` token only —
 * inside a code block, which is content rather than chrome, and where a keyword
 * hue distinct from the four blues is what makes a sample scannable. It reads
 * `--chart-4-text`, not `--chart-4`: the chart fill measures 3.25:1 as text on
 * `--card` and fails AA. The text step is Polymarket purple-800 (5.92:1).
 */
export const codePalette: TokenPalette = {
  keyword: "text-[hsl(var(--chart-4-text))]",
  string: "text-[hsl(var(--success-text))]",
  number: "text-[hsl(var(--destructive-text))]",
  comment: "italic text-muted-foreground",
  function: "text-[hsl(var(--primary-text))]",
  operator: "text-muted-foreground",
  punctuation: "text-muted-foreground",
  property: "text-[hsl(var(--info-text))]",
  type: "text-[hsl(var(--warning-text))]",
  plain: "text-foreground",
};

// prettier-ignore
const KEYWORDS = new Set([
  "async", "await", "break", "case", "catch", "class", "const", "continue",
  "default", "delete", "do", "else", "enum", "export", "extends", "false",
  "finally", "for", "from", "function", "if", "import", "in", "instanceof",
  "interface", "let", "new", "null", "of", "return", "static", "switch",
  "throw", "true", "try", "type", "typeof", "undefined", "var", "void",
  "while", "yield", "declare", "readonly",
]);

// prettier-ignore
const TYPES = new Set([
  "string", "number", "boolean", "any", "void", "never", "unknown", "object",
  "Array", "Promise", "Record", "Partial", "Required", "Pick", "Omit", "File",
  "Error", "Date", "Map", "Set", "AndroidNode", "Stage", "ScreenState",
  "ScreenshotRecord", "Agent", "AgentConstants", "AgentActions", "AgentUtils",
  "AgentInfo", "AgentControl", "AgentDisplay", "AgentEmail",
  "AgentNotifications", "AgentActionsTouch",
]);

/** Splits `code` into one token list per line. */
export function tokenize(code: string, language = "typescript"): Token[][] {
  if (language.toLowerCase() === "text") {
    return code.split("\n").map((line) => [{ type: "plain", content: line }]);
  }

  return code.split("\n").map((line) => {
    const tokens: Token[] = [];
    let i = 0;
    // `charAt` rather than `line[i]`: the project runs with
    // `noUncheckedIndexedAccess`, and every index here is already guarded by
    // the loop bound, so widening each read to `string | undefined` would only
    // add noise.
    const at = (n: number) => line.charAt(n);

    while (i < line.length) {
      if (/\s/.test(at(i))) {
        let ws = "";
        while (i < line.length && /\s/.test(at(i))) ws += at(i++);
        tokens.push({ type: "plain", content: ws });
        continue;
      }

      if (line.slice(i, i + 2) === "//") {
        tokens.push({ type: "comment", content: line.slice(i) });
        break;
      }

      // Quoted strings and template literals.
      if (at(i) === '"' || at(i) === "'" || at(i) === "`") {
        const quote = at(i);
        let str = quote;
        i++;
        while (i < line.length && at(i) !== quote) {
          if (at(i) === "\\" && i + 1 < line.length) str += at(i++);
          str += at(i++);
        }
        if (i < line.length) str += at(i++);
        tokens.push({ type: "string", content: str });
        continue;
      }

      if (/\d/.test(at(i))) {
        let num = "";
        while (i < line.length && /[\d.]/.test(at(i))) num += at(i++);
        tokens.push({ type: "number", content: num });
        continue;
      }

      if (/[a-zA-Z_$]/.test(at(i))) {
        let ident = "";
        while (i < line.length && /[a-zA-Z0-9_$]/.test(at(i))) ident += at(i++);

        const isCall = at(i) === "(";
        const isProperty =
          at(i) === ":" && !KEYWORDS.has(ident) && !TYPES.has(ident);

        if (KEYWORDS.has(ident))
          tokens.push({ type: "keyword", content: ident });
        else if (TYPES.has(ident))
          tokens.push({ type: "type", content: ident });
        else if (isCall) tokens.push({ type: "function", content: ident });
        else if (isProperty) tokens.push({ type: "property", content: ident });
        else tokens.push({ type: "plain", content: ident });
        continue;
      }

      if (/[+\-*/%=<>!&|^~?:]/.test(at(i))) {
        let op = "";
        while (i < line.length && /[+\-*/%=<>!&|^~?:]/.test(at(i)))
          op += at(i++);
        tokens.push({ type: "operator", content: op });
        continue;
      }

      if (/[{}[\](),;.]/.test(at(i))) {
        tokens.push({ type: "punctuation", content: at(i) });
        i++;
        continue;
      }

      tokens.push({ type: "plain", content: at(i) });
      i++;
    }

    return tokens;
  });
}

export const LANGUAGE_NAMES: Record<string, string> = {
  typescript: "TypeScript",
  ts: "TypeScript",
  javascript: "JavaScript",
  js: "JavaScript",
  json: "JSON",
  bash: "Bash",
  shell: "Shell",
  text: "Text",
};

export function languageLabel(language: string): string {
  return LANGUAGE_NAMES[language.toLowerCase()] ?? language.toUpperCase();
}

/** The tokenized lines, painted with the docs code theme. */
export function HighlightedCode({
  code,
  language = "typescript",
}: {
  code: string;
  language?: string;
}) {
  return (
    <code className="font-mono">
      {tokenize(code, language).map((tokens, line) => (
        <div key={line} className="min-h-[1.5em]">
          {tokens.length === 0
            ? "\n"
            : tokens.map((token, n) => (
                <span key={n} className={codePalette[token.type]}>
                  {token.content}
                </span>
              ))}
        </div>
      ))}
    </code>
  );
}
